import { Build, h, Host, writeTask } from "@stencil/core";
import { configureSlider } from "../abstract-uvex-slider/configure-slider";
import { ImageTypeInterface } from "../product-images/product-image-interfaces";
export class ProductImages {
    constructor() {
        this.images = [];
        this.imagesString = '';
        this.useSquareImages = true;
        this.mainSwiper = null;
        this.thumbnailSwiper = null;
    }
    async componentWillLoad() {
        if (this.imagesString !== '') {
            this.onImageUpdate(this.imagesString);
        }
    }
    async componentDidLoad() {
        if (Build.isBrowser) {
            await this.initSlider();
        }
    }
    onImageUpdate(newValue) {
        if (typeof newValue === 'string') {
            this.images = JSON.parse(newValue);
        }
        else {
            this.images = newValue;
        }
    }
    disconnectedCallback() {
        this.images = [];
        this.destroySlider();
    }
    connectedCallback() {
        this.update();
    }
    componentDidUpdate() {
        this.update();
    }
    async initSlider() {
        if (this.el.querySelectorAll('.carousel-slide').length === 0) {
            return;
        }
        if (!this.thumbnailSwiper) {
            this.thumbnailSwiper = await configureSlider(this.el.querySelector('.thumbnail-slider'), {
                slidesPerView: 'auto',
                spaceBetween: 8,
                watchSlidesProgress: true,
                freeMode: true,
                loop: false
            });
        }
        if (!this.mainSwiper) {
            this.mainSwiper = await configureSlider(this.el.querySelector('.main-slider'), {
                slidesPerView: 1,
                loop: false,
                thumbs: {
                    swiper: this.thumbnailSwiper
                }
            });
        }
        writeTask(() => this.update());
    }
    destroySlider() {
        if (this.thumbnailSwiper) {
            this.thumbnailSwiper.destroy();
        }
        if (this.mainSwiper) {
            this.mainSwiper.destroy();
        }
    }
    update() {
        if (this.mainSwiper) {
            this.mainSwiper.update();
            this.mainSwiper.slideTo(0, 0);
        }
        if (this.thumbnailSwiper) {
            this.thumbnailSwiper.update();
            this.thumbnailSwiper.slideTo(0, 0);
        }
    }
    openGallery(selectedImage) {
        if (selectedImage.type === ImageTypeInterface.iframe360) {
            return;
        }
        const modalElement = document.createElement('uvex-gallery-modal');
        const content = this.images.map(image => h("uvex-gallery-image", { image: image.zoom, active: true, transition: true }));
        Object.assign(modalElement, {
            content: h("uvex-gallery", { selectedImageIndex: selectedImage ? this.images.indexOf(selectedImage) : 0 }, content)
        });
        document.body.appendChild(modalElement);
    }
    renderMainImages() {
        const images = this.images.map((image, index) => {
            if ((image === null || image === void 0 ? void 0 : image.type) === ImageTypeInterface.iframe360) {
                const props = {
                    allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
                    allowfullscreen: true,
                    frameborder: '0',
                    src: image.src
                };
                return h("div", { class: "carousel-slide" }, h("div", { class: "product-image embed-responsive embed-responsive-1by1" }, h("iframe", Object.assign({}, props))));
            }
            let stlyes = {};
            if (!this.useSquareImages) {
                stlyes = {
                    'height': `${image.height}px`,
                };
            }
            return h("div", { class: "carousel-slide" }, h("uvex-zoom", { image: image.zoom, style: stlyes, onClick: _ => this.openGallery(image), useSquareImages: this.useSquareImages }, h("picture", { class: "ratio-picture", style: {
                    '--aspect-ratio': `${image.ratio}`,
                    '--width': `${image.width}px`,
                } }, h("source", { media: `(min-width: 1px)`, srcSet: image.src }), h("img", { fetchpriority: index == 0 ? 'high' : 'low', width: image.width, height: image.height, loading: "lazy", decoding: "async", class: "img-fluid", src: image.src, alt: image.altText, title: image.title }))));
        });
        return h("div", { class: "main-slider carousel carousel-shopware-style" }, h("div", { class: "carousel-inner" }, images), h("a", { class: "carousel-control carousel-control-left left disabled", role: "button" }), h("a", { class: {
                'carousel-control': true,
                'carousel-control-right': true,
                'right': true,
                'disabled': this.images.length <= 1,
            }, role: "button" }));
    }
    renderThumbnails() {
        const thumbnails = this.images.map(image => {
            return h("div", { class: "carousel-slide product-thumbnail", role: "button", style: { 'margin-right': '8px' } }, h("picture", { class: "ratio-picture", style: {
                    '--aspect-ratio': '1',
                    '--width': '62px',
                } }, h("source", { media: `(min-width: 1px)`, srcSet: image.src }), h("img", { fetchpriority: "low", loading: "lazy", decoding: "async", class: "img-fluid", width: "62", height: "62", src: image.thumbnail, alt: image.altText, title: image.title })));
        });
        return h("div", { class: "product-thumbnails" }, h("div", { class: "thumbnail-slider carousel carousel-initialized carousel-shopware-style" }, h("div", { class: "carousel-inner" }, thumbnails), h("a", { class: "carousel-control carousel-control-left left", role: "button" }), h("a", { class: "carousel-control carousel-control-right right", role: "button" })));
    }
    render() {
        return h(Host, { key: '1dba6175c2093bde3b9eed6ef363ddd9466ab7f0' }, h("slot", { key: '710bda273d894108b67dd16162453c4c4954fe16' }), this.renderMainImages(), this.renderThumbnails());
    }
    static get is() { return "uvex-product-images"; }
    static get properties() {
        return {
            "images": {
                "type": "unknown",
                "attribute": "images",
                "mutable": true,
                "complexType": {
                    "original": "ProductImage[]",
                    "resolved": "ProductImage[]",
                    "references": {
                        "ProductImage": {
                            "location": "import",
                            "path": "../product-images/product-image-interfaces",
                            "id": "source/js/components/product-images/product-image-interfaces.ts::ProductImage"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "defaultValue": "[]"
            },
            "imagesString": {
                "type": "string",
                "attribute": "images-string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "''"
            },
            "useSquareImages": {
                "type": "boolean",
                "attribute": "use-square-images",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "imagesString",
                "methodName": "onImageUpdate"
            }];
    }
}
//# sourceMappingURL=product-images.js.map
